<template>
  <div class="admin-container">
    <div class="admin-header">
      <div class="admin-header-titles">
        <h2>Comics</h2>
        <p class="comics-summary">{{ comicsSummaryLine }}</p>
      </div>
      <div class="admin-nav">
        <router-link to="/admin" class="nav-button">📅 Events</router-link>
        <router-link to="/admin_dates" class="nav-button">📊 Events</router-link>
        <router-link to="/admin_rota" class="nav-button">📋 Rota</router-link>
        <button class="logout-button" @click="logout">🚪 Logout</button>
      </div>
    </div>

    <!-- Add Rota Comic -->
    <section class="add-comic-section">
      <h3>Add Rota Comic</h3>
      <form class="add-comic-form" @submit.prevent="addRotaComic">
        <input type="email" v-model="newComic.email" placeholder="Email" required class="input-field" />
        <input type="text" v-model="newComic.name" placeholder="Name" required class="input-field" />
        <label class="checkbox-label"><input type="checkbox" v-model="newComic.is_mc" /> MC</label>
        <label class="checkbox-label"><input type="checkbox" v-model="newComic.is_admin_tech" /> Admin/Tech</label>
        <button type="submit" class="primary-button">Add</button>
      </form>
    </section>

    <!-- Filters & Sort -->
    <section>
      <div class="section-header">
        <div class="section-header-row">
          <div class="section-header-main">
            <h3>All Comics</h3>
            <input
              v-model="comicSearchQuery"
              type="search"
              class="comics-search-input"
              placeholder="Search name or email…"
              autocomplete="off"
              aria-label="Search comics by name or email"
            />
            <span v-if="searchFetchLoading" class="search-fetch-hint">Searching…</span>
          </div>
          <div class="filter-group">
          <div class="venue-filter">
            <label for="venue-select">Venue:</label>
            <select id="venue-select" v-model="selectedVenue" class="venue-select">
              <option value="all">All Venues</option>
              <option v-for="venue in venues" :key="venue.id" :value="venue.id">
                {{ venue.venue_name }}
              </option>
            </select>
          </div>
          <div class="venue-filter">
            <label for="status-filter">Status:</label>
            <select id="status-filter" v-model="selectedStatus" class="venue-select">
              <option value="all">All</option>
              <option v-for="(value, key) in APPLICATION_STATUSES" :key="key" :value="value">
                {{ getStatusLabel(value) }}
              </option>
            </select>
          </div>
          <label class="toggle-label">
            <input type="checkbox" v-model="showRejected" />
            Show rejected
          </label>
        </div>
        </div>
      </div>

      <div v-if="isLoading" class="loading">Loading...</div>

      <div v-else-if="groupedApplications.length === 0" class="no-applications">
        No comics found.
      </div>

      <div v-else-if="displayedGroupedComics.length === 0" class="no-applications">
        No comics match your search.
      </div>

      <!-- Comics Table -->
      <table v-else class="comics-table">
        <thead>
          <tr>
            <th class="sortable" @click="toggleSort('name')">
              Name {{ sortBy === 'name' ? '▲' : '' }}
            </th>
            <th>Email</th>
            <th>Roles</th>
            <th class="sortable" @click="toggleSort('recency')">
              Applications {{ sortBy === 'recency' ? '▼' : '' }}
            </th>
            <th>Video</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="comedian in displayedGroupedComics" :key="comedian.email">
            <tr
              class="comic-row"
              :class="{ 'blocked-row': isBlocked(comedian.email) }"
            >
              <td class="name-cell">
                <strong>{{ comedian.comedian_name }}</strong>
                <span v-if="isBlocked(comedian.email)" class="blocked-badge">blocked</span>
              </td>
              <td><a :href="`mailto:${comedian.email}`" class="email-link">{{ comedian.email }}</a></td>
              <td class="roles-cell">
                <label class="role-toggle" :title="comedian.is_mc ? 'Remove MC role' : 'Set as MC'">
                  <input
                    type="checkbox"
                    :checked="comedian.is_mc"
                    @change="toggleRole(comedian, 'is_mc', $event.target.checked)"
                  />
                  <span class="role-badge" :class="{ active: comedian.is_mc }">MC</span>
                </label>
                <label class="role-toggle" :title="comedian.is_admin_tech ? 'Remove Admin/Tech role' : 'Set as Admin/Tech'">
                  <input
                    type="checkbox"
                    :checked="comedian.is_admin_tech"
                    @change="toggleRole(comedian, 'is_admin_tech', $event.target.checked)"
                  />
                  <span class="role-badge" :class="{ active: comedian.is_admin_tech }">A/T</span>
                </label>
              </td>
              <td class="status-summary-cell">
                <span v-if="comedian.statusSummary.confirmed" class="status-pill confirmed">{{ comedian.statusSummary.confirmed }} confirmed</span>
                <span v-if="comedian.statusSummary.offered" class="status-pill offered">{{ comedian.statusSummary.offered }} offered</span>
                <span v-if="comedian.statusSummary.pending" class="status-pill pending">{{ comedian.statusSummary.pending }} pending</span>
                <span v-if="comedian.statusSummary.rejected && showRejected" class="status-pill rejected">{{ comedian.statusSummary.rejected }} rejected</span>
              </td>
              <td>
                <img
                  v-if="getVideoThumbnail(comedian.video_link)"
                  :src="getVideoThumbnail(comedian.video_link)"
                  class="video-thumbnail"
                  :alt="comedian.comedian_name + ' video'"
                  @click="previewVideo(comedian)"
                  loading="lazy"
                />
                <a
                  v-else
                  href="#"
                  @click.prevent="previewVideo(comedian)"
                  class="video-link-text"
                >🎥</a>
              </td>
              <td class="actions-cell">
                <button class="action-btn email-btn" @click="sendEmail(comedian)" title="Send booking email">📧</button>
                <button class="action-btn block-btn" @click="blockComedianAction(comedian)" title="Block">🚫</button>
                <button
                  class="action-btn expand-btn"
                  @click="toggleDetails(comedian)"
                  :title="isComicDetailsExpanded(comedian) ? 'Collapse' : 'Expand'"
                >{{ isComicDetailsExpanded(comedian) ? '▼' : '▶' }}</button>
              </td>
            </tr>

            <!-- Expanded detail row -->
            <tr v-if="isComicDetailsExpanded(comedian)" class="detail-row">
              <td colspan="6">
                <div class="detail-content">
                  <div class="detail-section">
                    <div v-if="comedian.facebook" class="detail-item">
                      <span class="detail-label">Facebook:</span>
                      <a :href="'https://www.facebook.com/search/people/?q=' + encodeURIComponent(comedian.facebook)" target="_blank" rel="noopener" class="social-link">{{ comedian.facebook }}</a>
                    </div>
                    <div v-if="comedian.instagram" class="detail-item">
                      <span class="detail-label">Instagram:</span>
                      <a :href="'https://instagram.com/' + sanitizeSocialUsername(comedian.instagram)" target="_blank" rel="noopener" class="social-link">@{{ sanitizeSocialUsername(comedian.instagram) }}</a>
                    </div>
                    <div v-if="comedian.notes" class="detail-item">
                      <span class="detail-label">Notes:</span> {{ comedian.notes }}
                    </div>
                  </div>

                  <div class="events-section">
                    <strong>Applications ({{ comedian.events.length }}):</strong>
                    <table class="events-sub-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Venue</th>
                          <th>Status</th>
                          <th>Slot</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="evt in comedian.events" :key="evt.application_id">
                          <td>{{ formatDate(evt.event_date) }}</td>
                          <td><span class="venue-tag">{{ evt.venue_name }}</span></td>
                          <td>
                            <select
                              class="status-select"
                              :style="{ borderColor: getStatusColor(evt.status), color: getStatusColor(evt.status) }"
                              :value="evt.status || 'pending'"
                              @change="updateStatus(evt, $event.target.value)"
                            >
                              <option v-for="(value, key) in APPLICATION_STATUSES" :key="key" :value="value">
                                {{ getStatusLabel(value) }}
                              </option>
                            </select>
                          </td>
                          <td>
                            <select
                              v-if="comedian.is_mc || comedian.is_admin_tech"
                              class="slot-type-select"
                              :value="evt.slot_type || 'performer'"
                              @change="updateSlotType(evt, $event.target.value)"
                            >
                              <option v-for="(label, value) in SLOT_TYPE_LABELS" :key="value" :value="value">
                                {{ label }}
                              </option>
                            </select>
                            <span v-else class="slot-label">{{ getSlotTypeLabel(evt.slot_type) }}</span>
                          </td>
                          <td>
                            <button
                              class="action-btn email-btn"
                              @click="sendEmailForEvent(comedian, evt)"
                              title="Send booking email for this date"
                            >📧</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Load More -->
      <div v-if="loadingMore" class="loading-more">Loading more...</div>
      <div v-if="!hasMoreData && groupedApplications.length > 0" class="end-message">
        All comics loaded
      </div>
    </section>

    <!-- Bulk Actions -->
    <section v-if="displayedGroupedComics.length > 0">
      <h3>Actions</h3>
      <div class="input-group">
        <button class="primary-button" @click="exportApplications">📊 Export CSV</button>
        <button class="secondary-button" @click="refreshApplications">🔄 Refresh</button>
      </div>
    </section>

    <!-- Blocked List -->
    <section>
      <h3>Blocked Emails ({{ blockedEmailsList.length }})</h3>
      <div v-if="blockedEmailsList.length === 0" class="no-applications">No blocked emails.</div>
      <div v-else class="blocked-list">
        <div v-for="entry in blockedEmailsList" :key="entry.id" class="blocked-item">
          <span class="blocked-email">{{ entry.email }}</span>
          <span v-if="entry.reason" class="blocked-reason">{{ entry.reason }}</span>
          <button class="unblock-button" @click="unblockEmailAction(entry)" title="Unblock">✅ Unblock</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
/** @see docs/APP_UI_SPEC.md — A1, A2, A3 (grouped comics, expand state, counter + search, ?q= deep link), B2 / B4 */
import { supabase } from '../supabaseClient';
import Swal from 'sweetalert2';
import { logoutAdmin } from '../utils/auth.js';
import { sanitizeSocialUsername } from '../utils/socialLinks.js';
import { getVideoThumbnailUrl } from '../utils/videoEmbed.js';
import { APPLICATION_STATUSES, getStatusLabel, getStatusColor, SLOT_TYPE_LABELS, getSlotTypeLabel } from '../utils/applicationStatus.js';
import {
  previewVideo as sharedPreviewVideo,
  blockComedian as sharedBlockComedian,
  unblockEmail as sharedUnblockEmail,
} from '../utils/adminActions.js';
import { runBookingOfferSend } from '../utils/bookingOfferSend.js';
import {
  applyAdminComicsSqlPrefilters,
  applicationMatchesAdminComicsFilters,
  buildComicsSummaryLine,
  filterGroupedComicsBySearch,
  stripIlikeWildcards,
} from '../utils/adminComicsList.js';

/** Same shape as `fetchApplicationsPage` / search merge (see A3). */
const APPLICATION_COMICS_JOIN_SELECT = `
  *,
  events (
    id,
    event_name,
    event_date,
    venue_id,
    venues (
      id,
      venue_name,
      venue_slug,
      address,
      google_maps_url,
      doors_time,
      show_time,
      show_end_time,
      has_spot_lengths
    )
  )
`;

export default {
  name: 'AdminComicsPage',
  data() {
    return {
      applications: [],
      venues: [],
      comedians: [],
      selectedVenue: 'all',
      selectedStatus: 'all',
      showRejected: false,
      sortBy: 'recency',
      blockedEmails: new Set(),
      blockedEmailsList: [],
      isLoading: true,
      loadingMore: false,
      currentPage: 0,
      pageSize: 100,
      hasMoreData: true,
      emailTemplate: null,
      newComic: { email: '', name: '', is_mc: false, is_admin_tech: false },
      /** Expanded comic detail rows — keyed by lowercased email (see APP_UI_SPEC A1). Not on computed groups. */
      comicsDetailExpanded: {},
      comicSearchQuery: '',
      totalComicsCount: null,
      totalCountLoading: false,
      searchFetchLoading: false,
      searchDebounceTimer: null,
      totalCountRequestGen: 0,
    };
  },
  computed: {
    APPLICATION_STATUSES() {
      return APPLICATION_STATUSES;
    },
    SLOT_TYPE_LABELS() {
      return SLOT_TYPE_LABELS;
    },
    /** Venue/status/rejected/blocked — shared by row filter, SQL prefilters, and distinct count (A3). */
    comicsFilterOpts() {
      return {
        blockedEmails: this.blockedEmails,
        selectedVenue: this.selectedVenue,
        selectedStatus: this.selectedStatus,
        showRejected: this.showRejected,
      };
    },
    filteredApplications() {
      const opts = this.comicsFilterOpts;
      return this.applications.filter(app =>
        applicationMatchesAdminComicsFilters(
          { email: app.email, status: app.status, venue_id: app.venue_id },
          opts
        )
      );
    },

    groupedApplications() {
      const comedianMap = new Map();

      this.filteredApplications.forEach(app => {
        const key = app.email?.toLowerCase();
        if (!key) return;

        if (!comedianMap.has(key)) {
          comedianMap.set(key, {
            comedian_name: app.comedian_name,
            email: app.email,
            video_link: app.video_link,
            facebook: app.facebook,
            instagram: app.instagram,
            notes: app.notes,
            events: [],
            maxId: app.id,
            statusSummary: {},
            is_mc: false,
            is_admin_tech: false,
          });
        }

        const comedian = comedianMap.get(key);
        comedian.events.push({
          application_id: app.id,
          event_id: app.event_id,
          event_name: app.event_name,
          event_date: app.event_date,
          venue_name: app.venue_name,
          venue_slug: app.venue_slug,
          status: app.status,
          slot_type: app.slot_type,
        });

        if (app.id > comedian.maxId) {
          comedian.maxId = app.id;
          comedian.comedian_name = app.comedian_name;
          comedian.email = app.email;
          comedian.video_link = app.video_link;
          comedian.facebook = app.facebook;
          comedian.instagram = app.instagram;
          comedian.notes = app.notes;
        }
      });

      // Compute status summaries and enrich with rota roles
      const comediansLookup = new Map(
        this.comedians.map(c => [c.email?.toLowerCase(), c])
      );

      for (const comedian of comedianMap.values()) {
        const summary = {};
        comedian.events.forEach(e => {
          const s = e.status || 'pending';
          summary[s] = (summary[s] || 0) + 1;
        });
        comedian.statusSummary = summary;

        const rotaRecord = comediansLookup.get(comedian.email?.toLowerCase());
        comedian.is_mc = rotaRecord?.is_mc || false;
        comedian.is_admin_tech = rotaRecord?.is_admin_tech || false;
        comedian.comedian_id = rotaRecord?.id || null;
      }

      const result = Array.from(comedianMap.values());

      if (this.sortBy === 'name') {
        result.sort((a, b) => a.comedian_name.localeCompare(b.comedian_name));
      } else {
        result.sort((a, b) => b.maxId - a.maxId);
      }

      return result;
    },

    displayedGroupedComics() {
      return filterGroupedComicsBySearch(this.groupedApplications, this.comicSearchQuery);
    },

    comicsSummaryLine() {
      return buildComicsSummaryLine({
        loadedGroupCount: this.groupedApplications.length,
        totalCount: this.totalComicsCount,
        totalLoading: this.totalCountLoading,
        hasMoreData: this.hasMoreData,
        searchTerm: this.comicSearchQuery,
        matchingCount: this.displayedGroupedComics.length,
      });
    },
  },
  watch: {
    comicSearchQuery() {
      this.scheduleSearchFetch();
    },
    selectedVenue() {
      this.refreshTotalComicsCount();
    },
    selectedStatus() {
      this.refreshTotalComicsCount();
    },
    showRejected() {
      this.refreshTotalComicsCount();
    },
  },
  async created() {
    await Promise.all([
      this.loadBlockedEmails(),
      this.loadVenues(),
      this.loadComedians(),
      this.loadEmailTemplate(),
    ]);
    await this.loadApplications();
    this.applyComicsSearchFromRouteQuery();
    this.setupInfiniteScroll();
  },
  beforeUnmount() {
    this.removeInfiniteScroll();
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    applyComicsSearchFromRouteQuery() {
      const raw = this.$route?.query?.q;
      const q = typeof raw === 'string' ? raw.trim() : '';
      if (!q) return;
      this.comicSearchQuery = q;
      void this.fetchSearchMatchesIfNeeded();
    },

    normalizeRawApplication(app) {
      return {
        ...app,
        event_id: app.events.id,
        event_name: app.events.event_name,
        event_date: app.events.event_date,
        venue_id: app.events.venue_id,
        venue_name: app.events.venues?.venue_name || 'Unknown Venue',
        venue_slug: app.events.venues?.venue_slug || '',
        _event_venues: app.events.venues,
      };
    },

    scheduleSearchFetch() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.searchDebounceTimer = null;
        this.fetchSearchMatchesIfNeeded();
      }, 300);
    },

    mergeNormalizedApplications(normalizedRows) {
      const byId = new Map(this.applications.map(a => [a.id, a]));
      for (const row of normalizedRows) {
        byId.set(row.id, row);
      }
      this.applications = Array.from(byId.values()).sort((a, b) => b.id - a.id);
    },

    async refreshTotalComicsCount() {
      this.totalCountRequestGen += 1;
      const gen = this.totalCountRequestGen;
      this.totalCountLoading = true;
      try {
        const opts = this.comicsFilterOpts;
        const distinct = new Set();
        const BATCH = 500;
        let start = 0;
        for (;;) {
          let q = supabase
            .from('applications')
            .select('id, email, status, events!inner(venue_id)')
            .order('id', { ascending: false })
            .range(start, start + BATCH - 1);
          q = applyAdminComicsSqlPrefilters(q, opts);
          const { data, error } = await q;
          if (error) throw error;
          if (gen !== this.totalCountRequestGen) return;
          const rows = data || [];
          for (const row of rows) {
            const flat = {
              email: row.email,
              status: row.status,
              venue_id: row.events?.venue_id,
            };
            if (applicationMatchesAdminComicsFilters(flat, opts)) {
              distinct.add(String(row.email).toLowerCase());
            }
          }
          if (rows.length < BATCH) break;
          start += BATCH;
        }
        if (gen !== this.totalCountRequestGen) return;
        this.totalComicsCount = distinct.size;
      } catch (err) {
        console.error('Error counting comics:', err);
        if (gen === this.totalCountRequestGen) this.totalComicsCount = null;
      } finally {
        if (gen === this.totalCountRequestGen) this.totalCountLoading = false;
      }
    },

    async fetchSearchMatchesIfNeeded() {
      const rawQ = this.comicSearchQuery;
      const cleaned = stripIlikeWildcards(rawQ).replace(/,/g, '').trim();
      if (!cleaned) return;
      if (filterGroupedComicsBySearch(this.groupedApplications, rawQ).length > 0) return;

      this.searchFetchLoading = true;
      try {
        const pattern = `%${cleaned}%`;
        const opts = this.comicsFilterOpts;
        let qName = supabase
          .from('applications')
          .select(APPLICATION_COMICS_JOIN_SELECT)
          .ilike('comedian_name', pattern)
          .order('id', { ascending: false })
          .limit(150);
        qName = applyAdminComicsSqlPrefilters(qName, opts);

        let qEmail = supabase
          .from('applications')
          .select(APPLICATION_COMICS_JOIN_SELECT)
          .ilike('email', pattern)
          .order('id', { ascending: false })
          .limit(150);
        qEmail = applyAdminComicsSqlPrefilters(qEmail, opts);

        const [rName, rEmail] = await Promise.all([qName, qEmail]);
        if (rName.error) throw rName.error;
        if (rEmail.error) throw rEmail.error;

        const mergedRaw = new Map();
        for (const row of rName.data || []) mergedRaw.set(row.id, row);
        for (const row of rEmail.data || []) mergedRaw.set(row.id, row);

        const normalized = [];
        for (const row of mergedRaw.values()) {
          if (!row.events) continue;
          normalized.push(this.normalizeRawApplication(row));
        }
        this.mergeNormalizedApplications(normalized);
      } catch (err) {
        console.error('Error searching comics:', err);
        Swal.fire('Error', 'Search failed', 'error');
      } finally {
        this.searchFetchLoading = false;
      }
    },
    async loadBlockedEmails() {
      try {
        const { data, error } = await supabase
          .from('blocked_emails')
          .select('id, email, reason, created_at')
          .order('created_at', { ascending: false });

        if (error) throw error;
        this.blockedEmailsList = data || [];
        this.blockedEmails = new Set(this.blockedEmailsList.map(row => row.email.toLowerCase()));
      } catch (err) {
        console.error('Error loading blocked emails:', err);
      }
    },

    async loadVenues() {
      try {
        const { data, error } = await supabase
          .from('venues')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) throw error;
        this.venues = data || [];
      } catch (err) {
        console.error('Error loading venues:', err);
      }
    },

    async loadComedians() {
      try {
        const { data, error } = await supabase
          .from('comedians')
          .select('id, email, name, is_mc, is_admin_tech');

        if (error) throw error;
        this.comedians = data || [];
      } catch (err) {
        console.error('Error loading comedians:', err);
      }
    },

    async loadEmailTemplate() {
      try {
        const { data, error } = await supabase
          .from('email_templates')
          .select('*')
          .eq('id', 1)
          .single();

        if (error) {
          console.error('Error loading email template:', error);
          return;
        }
        this.emailTemplate = data;
      } catch (err) {
        console.error('Error loading email template:', err);
      }
    },

    async loadApplications() {
      this.isLoading = true;
      this.currentPage = 0;
      this.applications = [];
      this.hasMoreData = true;

      try {
        await this.fetchApplicationsPage();
        await this.refreshTotalComicsCount();
      } catch (err) {
        console.error('Unexpected error:', err);
        Swal.fire('Error', 'An unexpected error occurred', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async fetchApplicationsPage() {
      const startRange = this.currentPage * this.pageSize;
      const endRange = startRange + this.pageSize - 1;

      const { data: applications, error } = await supabase
        .from('applications')
        .select(APPLICATION_COMICS_JOIN_SELECT)
        .order('id', { ascending: false })
        .range(startRange, endRange);

      if (error) {
        console.error('Error fetching applications:', error);
        throw error;
      }

      const newApplications = applications.map(app => this.normalizeRawApplication(app));

      this.applications = [...this.applications, ...newApplications];

      if (applications.length < this.pageSize) {
        this.hasMoreData = false;
      }

      this.currentPage++;
    },

    async loadMoreApplications() {
      if (this.loadingMore || !this.hasMoreData) return;

      this.loadingMore = true;
      try {
        await this.fetchApplicationsPage();
      } catch (err) {
        console.error('Error loading more applications:', err);
        Swal.fire('Error', 'Failed to load more applications', 'error');
      } finally {
        this.loadingMore = false;
      }
    },

    toggleSort(field) {
      this.sortBy = field;
    },

    comicDetailKey(comedian) {
      return (comedian.email || '').trim().toLowerCase();
    },
    isComicDetailsExpanded(comedian) {
      const k = this.comicDetailKey(comedian);
      return Boolean(k && this.comicsDetailExpanded[k]);
    },
    toggleDetails(comedian) {
      const k = this.comicDetailKey(comedian);
      if (!k) return;
      const next = !this.isComicDetailsExpanded(comedian);
      this.comicsDetailExpanded = { ...this.comicsDetailExpanded, [k]: next };
    },

    isBlocked(email) {
      return this.blockedEmails.has(email?.toLowerCase());
    },

    previewVideo(comedian) {
      sharedPreviewVideo(comedian);
    },

    async sendEmail(comedian) {
      if (comedian.events.length === 0) return;

      if (comedian.events.length === 1) {
        await this.sendEmailForEvent(comedian, comedian.events[0]);
        return;
      }

      const options = {};
      comedian.events.forEach(evt => {
        options[evt.application_id] = `${evt.event_name} — ${this.formatDate(evt.event_date)} (${evt.venue_name})`;
      });

      const { value: selectedId } = await Swal.fire({
        title: 'Which date?',
        input: 'select',
        inputOptions: options,
        inputPlaceholder: 'Select a date to book for',
        showCancelButton: true,
      });

      if (!selectedId) return;
      const evt = comedian.events.find(e => e.application_id == selectedId);
      if (evt) await this.sendEmailForEvent(comedian, evt);
    },

    async sendEmailForEvent(comedian, evt) {
      const venueData = this.findVenueForEvent(evt);
      const eventObj = {
        event_name: evt.event_name,
        event_date: evt.event_date,
        venues: venueData,
      };
      try {
        await runBookingOfferSend({
          supabase,
          application: comedian,
          event: eventObj,
          emailTemplate: this.emailTemplate,
          applicationId: evt.application_id,
          onOffered: async (spot) => {
            evt.offer_spot_code = spot.spotCode;
            evt.offer_is_bringer = spot.isBringer;
            await this.updateStatus(evt, APPLICATION_STATUSES.OFFERED);
          },
        });
      } catch (error) {
        console.error('Error creating email:', error);
        Swal.fire('Error', 'Failed to create email', 'error');
      }
    },

    findVenueForEvent(evt) {
      const app = this.applications.find(a => a.event_id === evt.event_id);
      return app?._event_venues || null;
    },

    async blockComedianAction(comedian) {
      const { blocked } = await sharedBlockComedian(comedian);
      if (blocked) {
        this.blockedEmails.add(comedian.email.toLowerCase());
        await this.loadBlockedEmails();
        await this.refreshTotalComicsCount();
      }
    },

    async unblockEmailAction(entry) {
      const { unblocked } = await sharedUnblockEmail(entry);
      if (unblocked) {
        await this.loadBlockedEmails();
        await this.loadApplications();
      }
    },

    async updateStatus(event, newStatus) {
      try {
        const { error } = await supabase
          .from('applications')
          .update({ status: newStatus })
          .eq('id', event.application_id);

        if (error) throw error;
        event.status = newStatus;
      } catch (err) {
        console.error('Error updating status:', err);
        Swal.fire('Error', 'Failed to update application status', 'error');
      }
    },

    async updateSlotType(event, newSlotType) {
      try {
        const { error } = await supabase
          .from('applications')
          .update({ slot_type: newSlotType })
          .eq('id', event.application_id);

        if (error) throw error;
        event.slot_type = newSlotType;
      } catch (err) {
        console.error('Error updating slot type:', err);
        Swal.fire('Error', 'Failed to update slot type', 'error');
      }
    },

    async toggleRole(comedian, field, checked) {
      try {
        if (comedian.comedian_id) {
          const { error } = await supabase
            .from('comedians')
            .update({ [field]: checked })
            .eq('id', comedian.comedian_id);

          if (error) throw error;
        } else {
          const { data, error } = await supabase
            .from('comedians')
            .upsert({
              email: comedian.email.toLowerCase(),
              name: comedian.comedian_name,
              [field]: checked,
            }, { onConflict: 'email' })
            .select()
            .single();

          if (error) throw error;
          comedian.comedian_id = data?.id || null;
        }
        comedian[field] = checked;
        await this.loadComedians();
      } catch (err) {
        console.error('Error toggling role:', err);
        Swal.fire('Error', 'Failed to update role', 'error');
      }
    },

    async addRotaComic() {
      const { email, name, is_mc, is_admin_tech } = this.newComic;
      if (!email || !name) return;

      try {
        const { error } = await supabase
          .from('comedians')
          .upsert({
            email: email.toLowerCase(),
            name,
            is_mc,
            is_admin_tech,
          }, { onConflict: 'email' });

        if (error) throw error;

        this.newComic = { email: '', name: '', is_mc: false, is_admin_tech: false };
        await this.loadComedians();
        Swal.fire('Success', `${name} added as rota comic`, 'success');
      } catch (err) {
        console.error('Error adding rota comic:', err);
        Swal.fire('Error', 'Failed to add comic', 'error');
      }
    },

    async refreshApplications() {
      await Promise.all([this.loadApplications(), this.loadComedians()]);
      Swal.fire('Success', 'Data refreshed!', 'success');
    },

    setupInfiniteScroll() {
      this.handleScroll = () => {
        if (this.loadingMore || !this.hasMoreData) return;

        const scrollPosition = window.innerHeight + window.scrollY;
        const documentHeight = document.documentElement.offsetHeight;

        if (scrollPosition >= documentHeight - 200) {
          this.loadMoreApplications();
        }
      };
      window.addEventListener('scroll', this.handleScroll);
    },

    removeInfiniteScroll() {
      if (this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll);
      }
    },

    exportApplications() {
      try {
        const csvData = this.displayedGroupedComics.map(comedian => ({
          'Name': comedian.comedian_name,
          'Email': comedian.email,
          'MC': comedian.is_mc ? 'Yes' : '',
          'Admin/Tech': comedian.is_admin_tech ? 'Yes' : '',
          'Events Applied For': comedian.events.map(e => `${e.event_name} (${this.formatDate(e.event_date)})`).join('; '),
          'Video Link': comedian.video_link,
          'Facebook': comedian.facebook || '',
          'Instagram': comedian.instagram || '',
          'Notes': comedian.notes || '',
        }));

        const csvString = this.convertToCSV(csvData);
        this.downloadCSV(csvString, 'comedy-comics.csv');
      } catch (error) {
        console.error('Error exporting data:', error);
        Swal.fire('Error', 'Failed to export data', 'error');
      }
    },

    convertToCSV(data) {
      if (!data.length) return '';
      const headers = Object.keys(data[0]);
      const csvRows = [
        headers.join(','),
        ...data.map(row =>
          headers.map(header => {
            const value = row[header] || '';
            return `"${value.toString().replace(/"/g, '""')}"`;
          }).join(',')
        )
      ];
      return csvRows.join('\n');
    },

    downloadCSV(csvString, filename) {
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    },

    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    getStatusLabel(status) { return getStatusLabel(status); },
    getStatusColor(status) { return getStatusColor(status); },
    getSlotTypeLabel(slotType) { return getSlotTypeLabel(slotType); },
    getVideoThumbnail(url) { return getVideoThumbnailUrl(url); },
    sanitizeSocialUsername(username) { return sanitizeSocialUsername(username); },

    logout() {
      logoutAdmin();
      this.$router.push('/');
    },
  }
};
</script>

<style scoped>
.admin-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.admin-header-titles { max-width: 65%; }

.admin-header h2 { margin: 0 0 6px 0; color: #333; }

.comics-summary {
  margin: 0;
  font-size: 14px;
  color: #555;
  line-height: 1.4;
}

.section-header-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.section-header-main h3 { margin: 0; }

.comics-search-input {
  min-width: 200px;
  flex: 1;
  max-width: 320px;
  padding: 8px 12px;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}

.comics-search-input:focus {
  outline: none;
  border-color: #007bff;
}

.search-fetch-hint {
  font-size: 13px;
  color: #888;
  font-style: italic;
}

.admin-nav { display: flex; gap: 10px; align-items: center; }

.nav-button {
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 14px;
  transition: background-color 0.2s;
}
.nav-button:hover { background: #5a6268; }
.nav-button.router-link-active { background: #007bff; }

.logout-button {
  padding: 8px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.logout-button:hover { background: #c82333; }

section {
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

h3 { margin-top: 0; margin-bottom: 15px; color: #34495e; }

/* Add Comic Form */
.add-comic-section { padding: 15px 20px; }
.add-comic-form {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.input-field {
  padding: 8px 12px;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}
.input-field:focus { outline: none; border-color: #007bff; }
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

/* Filters */
.section-header {
  margin-bottom: 15px;
}
.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.filter-group { display: flex; gap: 15px; flex-wrap: wrap; align-items: center; }
.venue-filter { display: flex; align-items: center; gap: 8px; }
.venue-filter label { font-weight: 600; color: #666; font-size: 13px; }
.venue-select {
  padding: 6px 10px;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 13px;
  background: white;
  cursor: pointer;
}
.venue-select:focus { outline: none; border-color: #007bff; }
.toggle-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
}

/* Comics Table */
.comics-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.comics-table th {
  text-align: left;
  padding: 10px 8px;
  border-bottom: 2px solid #dee2e6;
  color: #495057;
  font-size: 13px;
  white-space: nowrap;
}
.comics-table th.sortable {
  cursor: pointer;
  user-select: none;
}
.comics-table th.sortable:hover { color: #007bff; }

.comic-row td {
  padding: 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}
.comic-row:hover { background: #f8f9fa; }
.comic-row.blocked-row { opacity: 0.5; }

.name-cell strong { color: #2c3e50; }
.blocked-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  background: #dc3545;
  color: white;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}
.email-link { color: #007bff; text-decoration: none; font-size: 13px; }
.email-link:hover { text-decoration: underline; }

/* Role toggles */
.roles-cell { white-space: nowrap; }
.role-toggle {
  cursor: pointer;
  margin-right: 4px;
}
.role-toggle input { display: none; }
.role-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  border: 2px solid #ccc;
  color: #999;
  transition: all 0.2s;
}
.role-badge.active {
  border-color: #007bff;
  color: #007bff;
  background: #e7f1ff;
}
.role-badge:hover { border-color: #007bff; }

/* Status summary pills */
.status-summary-cell { white-space: nowrap; }
.status-pill {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 4px;
}
.status-pill.confirmed { background: #d4edda; color: #155724; }
.status-pill.offered { background: #fff3cd; color: #856404; }
.status-pill.pending { background: #e2e3e5; color: #383d41; }
.status-pill.rejected { background: #f8d7da; color: #721c24; }

/* Video thumbnail */
.video-thumbnail {
  width: 60px;
  height: 34px;
  object-fit: cover;
  border-radius: 3px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
.video-thumbnail:hover { opacity: 0.85; transform: scale(1.05); }
.video-link-text { font-size: 16px; text-decoration: none; }

/* Action buttons */
.actions-cell { white-space: nowrap; }
.action-btn {
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 3px;
  transition: background-color 0.2s;
}
.email-btn { background: #007bff; color: white; }
.email-btn:hover { background: #0056b3; }
.block-btn { background: #c8cbce; color: white; }
.block-btn:hover { background: #dc3545; }
.expand-btn { background: #6c757d; color: white; }
.expand-btn:hover { background: #545b62; }

/* Expanded detail row */
.detail-row td {
  padding: 0 8px 12px;
  background: #fafafa;
  border-bottom: 2px solid #dee2e6;
}
.detail-content {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 20px;
  padding: 12px;
}
.detail-section { font-size: 13px; }
.detail-item { margin-bottom: 6px; }
.detail-label { font-weight: 600; color: #666; margin-right: 4px; }
.social-link { color: #007bff; text-decoration: none; }
.social-link:hover { text-decoration: underline; }

/* Events sub-table */
.events-sub-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 6px;
}
.events-sub-table th {
  text-align: left;
  padding: 4px 6px;
  border-bottom: 1px solid #ddd;
  color: #666;
  font-size: 11px;
}
.events-sub-table td {
  padding: 4px 6px;
  border-bottom: 1px solid #f0f0f0;
}
.venue-tag {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}

.status-select {
  padding: 2px 6px;
  border: 2px solid #6c757d;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: white;
  cursor: pointer;
}
.status-select:focus { outline: none; }

.slot-type-select {
  padding: 2px 6px;
  border: 2px solid #17a2b8;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: white;
  cursor: pointer;
  color: #17a2b8;
}
.slot-type-select:focus { outline: none; }
.slot-label { font-size: 11px; color: #999; }

/* Misc */
.loading { text-align: center; padding: 40px; color: #666; font-size: 16px; }
.no-applications { text-align: center; padding: 20px; color: #666; }
.loading-more { text-align: center; padding: 15px; color: #999; font-style: italic; }
.end-message { text-align: center; padding: 15px; color: #999; font-style: italic; }

.input-group { display: flex; gap: 10px; flex-wrap: wrap; }
.primary-button {
  padding: 10px 20px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.primary-button:hover { background: #0056b3; }
.secondary-button {
  padding: 10px 20px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.secondary-button:hover { background: #545b62; }

/* Blocked list */
.blocked-list { display: flex; flex-direction: column; gap: 8px; }
.blocked-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #fff3f3;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
}
.blocked-email { font-weight: 600; color: #721c24; }
.blocked-reason { color: #856404; font-size: 13px; font-style: italic; }
.unblock-button {
  margin-left: auto;
  padding: 4px 12px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
.unblock-button:hover { background: #218838; }

@media (max-width: 768px) {
  .admin-header { flex-direction: column; gap: 15px; text-align: center; }
  .admin-header-titles { max-width: 100%; }
  .admin-nav { flex-wrap: wrap; justify-content: center; }
  .section-header-row { flex-direction: column; align-items: flex-start; }
  .add-comic-form { flex-direction: column; }
  .comics-table { font-size: 12px; }
  .detail-content { grid-template-columns: 1fr; }
}
</style>
